const ROLE_ALIASES = {
  feeder: ["specialist-feeder", "feeder"],
  "pre-manager": ["specialist-pre-manager", "pre-manager"],
  manager: ["specialist-manager", "manager"],
  validator: ["specialist-verificator", "specialist-validator", "validator", "verificator"],
  editor: ["specialist-editor", "editor"],
  liaison: ["specialist-liaison", "liaison"],
  coordinator: ["specialist-coordinator", "coordinator"]
};

const ROLE_IDS = {
  feeder: 3,
  "pre-manager": 4,
  manager: 5,
  validator: 6,
  editor: 7,
  coordinator: 8,
  liaison: 9
};

export const DIAGRAM_CHAIN_ROLES = [
  { key: "feeder", color: "feeder" },
  { key: "pre-manager", color: "pre" },
  { key: "manager", color: "manager" },
  { key: "validator", color: "validator" },
  { key: "editor", color: "editor" }
];

export const DIAGRAM_HOPS = [
  { from: "feeder", to: "pre-manager" },
  { from: "pre-manager", to: "manager" },
  { from: "manager", to: "validator" },
  { from: "validator", to: "editor" }
];

export const DIAGRAM_ISOLATED_ROLES = [
  { key: "liaison", color: "liaison" },
  { key: "coordinator", color: "coordinator" }
];

export const DIAGRAM_RULE_CODES = [
  "sequential-round-robin",
  "workload",
  "assignment-pool",
  "manual-tree"
];

const normalizeToken = (value) => String(value || "").trim().toLowerCase();

export const resolveDiagramRoleKey = (roleLike) => {
  if (!roleLike && roleLike !== 0) {
    return "";
  }
  if (typeof roleLike === "object") {
    const fromName = resolveDiagramRoleKey(roleLike.name || roleLike.key);
    if (fromName) {
      return fromName;
    }
    return resolveDiagramRoleKey(roleLike.id);
  }
  const token = normalizeToken(roleLike);
  const asNumber = Number(roleLike);
  if (Number.isFinite(asNumber) && asNumber > 0) {
    const byId = Object.entries(ROLE_IDS).find(([, id]) => Number(id) === asNumber);
    if (byId) {
      return byId[0];
    }
  }
  return Object.keys(ROLE_ALIASES).find((key) => (
    key === token || ROLE_ALIASES[key].includes(token)
  )) || "";
};

export const sortPolicyRules = (policy) => (
  [...(policy?.FlowAssignmentPolicyRule_To_FlowAssignmentPolicy || [])]
    .filter((rule) => !rule.deleted)
    .sort((left, right) => Number(left.sort_order || 0) - Number(right.sort_order || 0))
);

export const findWinningRule = (rules = []) => (
  rules.find((rule) => rule.enabled !== false) || null
);

export const findHopPolicy = (policies = [], catalogmoduleId, fromKey, toKey, direction) => {
  const moduleId = Number(catalogmoduleId);
  return (policies || []).find((policy) => {
    if (Number(policy.catalogmodule_id) !== moduleId) {
      return false;
    }
    if (normalizeToken(policy.direction) !== normalizeToken(direction)) {
      return false;
    }
    const fromRole = resolveDiagramRoleKey(
      policy.FromCatalogRole_To_FlowAssignmentPolicy || policy.from_catalogrole_id
    );
    const toRole = resolveDiagramRoleKey(
      policy.ToCatalogRole_To_FlowAssignmentPolicy || policy.to_catalogrole_id
    );
    return fromRole === fromKey && toRole === toKey;
  }) || null;
};

export const buildDisplayedRules = (policy) => {
  const actual = sortPolicyRules(policy);
  if (!actual.length) {
    return DIAGRAM_RULE_CODES.map((code, index) => ({
      code,
      sort_order: index + 1,
      enabled: false,
      isWinner: false
    }));
  }
  const winner = findWinningRule(actual);
  return actual.map((rule) => ({
    id: rule.id,
    code: rule.rule_code || rule.code,
    sort_order: rule.sort_order,
    enabled: rule.enabled,
    isWinner: Boolean(winner && Number(winner.id) === Number(rule.id))
  }));
};

export const formatDiagramUser = (user) => {
  if (!user) {
    return "";
  }
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
  if (user.user_name && fullName) {
    return `${user.user_name} — ${fullName}`;
  }
  return user.user_name || fullName || user.email || String(user.id || "");
};

const buildUserPool = (snapshot, extraUsers = []) => {
  const byId = new Map();
  (extraUsers || []).forEach((user) => {
    if (user?.id) {
      byId.set(Number(user.id), user);
    }
  });
  (snapshot?.eligibleUsers || []).forEach((user) => {
    if (user?.id) {
      byId.set(Number(user.id), user);
    }
  });
  return byId;
};

export const collectCoverageUsers = (snapshot, extraUsers = []) => {
  const usersById = buildUserPool(snapshot, extraUsers);
  const groups = snapshot?.flowCoverageGroups || [];
  return groups.map((group) => {
    const members = [...(group.FlowCoverageMember_To_FlowCoverageGroup || [])]
      .filter((member) => !member.deleted)
      .sort((left, right) => Number(left.sequential_order || 0) - Number(right.sequential_order || 0))
      .map((member, index) => ({
        id: member.id,
        userId: member.user_id,
        order: member.sequential_order || index + 1,
        active: member.active !== false,
        user: member.User_To_FlowCoverageMember
          || usersById.get(Number(member.user_id))
          || { id: member.user_id }
      }));
    return {
      id: group.id,
      name: group.name || group.code || "",
      members
    };
  });
};

export const collectFallbackUsers = (users = []) => (
  (users || []).map((user, index) => ({
    id: user.id,
    userId: user.id,
    order: index + 1,
    active: user.deleted !== true,
    user
  }))
);
